import React, { useContext, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
// Import contexts
import { FormDataContext } from 'globalState/FormDataContext';

// Import components
import Step1Enquiry from './Step1/EnquirySelection';
// Import mayor steps
import MayorStep1Enquiry from './Step2/Mayor/EnquiryType';
import MayorStep1Event from './Step3/Mayor/Invite/Event';
import MayorStep4Event from './Step4/Mayor/Invite/Who';
import MayorGeneralStep6Enquiry from './StepConfirm/Mayor/Enquiry/StepConfirm';
import MayorGeneralStep6Invite from './StepConfirm/Mayor/Invite/StepConfirm';

// Strategy
import StrategyEnquiryType from './Step2/Strategy/EnquiryType';

// Other
import OtherEnquiryType from './Step2/Other/EnquiryType';
import OtherFoi from './Step3/Other/FOI/Foi';
import OtherFraud from './Step3/Other/Fraud/Fraud';
import OtherJobs from './Step3/Other/Jobs/Jobs';

// confirmations
import GeneralConfirmation from './StepConfirm/General/StepConfirm';
import StrategyConfirmation from './StepConfirm/Strategy/StepConfirm';
import OtherFoiConfirmation from './StepConfirm/Other/Foi/StepConfirm';
import OtherFraudConfirmation from './StepConfirm/Other/Fraud/StepConfirm';
import OtherJobsConfirmation from './StepConfirm/Other/Jobs/StepConfirm';

// shared
import SharedEnquiry from './Shared/Enquiry/Enquiry';
import SharedContact from './Shared/Contact/Contact';
import SharedName from './Shared/Name/Fieldset/Name';

// Custom Hooks
import useTrackFormAbandonment from './useTrackFormAbandonment';
// Import styling
import s from './Form.module.scss';

const Form = ({ formSubmitStatus, setFormSubmitStatus, isRecoverLinkPressed }) => {
  // Handle scrolling to the top of the summary page
  const formRef = useRef();
  const scrollToTopOfSummary = useCallback(() => {
    const page = document.getElementsByClassName('wmcads-html')[0];
    const pageOffset = page.scrollTop;
    const formOffset = formRef.current.offsetTop;
    if (formOffset >= pageOffset) return;
    page.scrollTo(0, formOffset - 20);
  }, []);

  const [formDataState, formDataDispatch] = useContext(FormDataContext); // Get the state/dispatch of form data from FormDataContext
  const { currentStep } = formDataState; // Destructure step from state
  const { EnquiryType, StrategyEnquiry, MayorEnquiry, OtherEnquiry } = formDataState.formData;
  const methods = useForm({
    mode: 'onBlur',
  }); // Trigger validation onBlur events (config for react hook form lib)

  useEffect(() => {
    if (currentStep === 9) scrollToTopOfSummary();
  }, [currentStep, scrollToTopOfSummary]);

  useEffect(() => {
    if (isRecoverLinkPressed) {
      formDataDispatch({
        type: 'UPDATE_STEP',
        payload: 0,
      });
    }
  }, [formDataDispatch, isRecoverLinkPressed]);

  useTrackFormAbandonment(currentStep, formSubmitStatus);

  // Show debug options for below (this should be deleted on release)
  const debugStepOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  let stepToGoTo;

  if (currentStep > 1 && currentStep < 6) {
    stepToGoTo = currentStep - 1;
  }

  // Run! Like go get some data from an API.
  return (
    <>
      {/* pass all methods into the context */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <div className="wmcads-col-1 wmcads-col-md-2-3">
          {stepToGoTo && (
            <div className="wmcads-col-1 wmcads-m-b-md">
              <button
                type="button"
                className="wmcads-btn wmcads-btn--link"
                onClick={() =>
                  formDataDispatch({
                    type: 'UPDATE_STEP',
                    payload: stepToGoTo,
                  })
                }
              >
                &lt; Back
              </button>
            </div>
          )}

          <div
            className={formSubmitStatus === null ? `${s.formWrapper} wmcads-p-lg ` : ''}
            ref={formRef}
          >
            {/* Start of form */}
            {currentStep === 1 && <Step1Enquiry setFormSubmitStatus={setFormSubmitStatus} />}

            {/* Mayor enquiry type */}
            {currentStep === 2 && EnquiryType === 'mayor' && <MayorStep1Enquiry />}

            {/* Mayor general enquiry */}
            {currentStep === 3 && MayorEnquiry === 'An enquiry for the Mayor’s office' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry for the Mayor’s office?"
              />
            )}
            {currentStep === 4 && MayorEnquiry === 'An enquiry for the Mayor’s office' && (
              <SharedName section="Section 3 of 4" header="About you" />
            )}
            {currentStep === 5 && MayorEnquiry === 'An enquiry for the Mayor’s office' && (
              <SharedContact section="Section 4 of 4" header="About you" />
            )}
            {currentStep === 6 && MayorEnquiry === 'An enquiry for the Mayor’s office' && (
              <MayorGeneralStep6Enquiry setFormSubmitStatus={setFormSubmitStatus} />
            )}

            {/* Mayor event invite */}
            {currentStep === 3 && MayorEnquiry === 'Invite the Mayor to an event' && (
              <MayorStep1Event />
            )}
            {currentStep === 4 && MayorEnquiry === 'Invite the Mayor to an event' && (
              <MayorStep4Event />
            )}
            {currentStep === 5 && MayorEnquiry === 'Invite the Mayor to an event' && (
              <SharedContact section="Section 4 of 4" header="About you" />
            )}
            {currentStep === 6 && MayorEnquiry === 'Invite the Mayor to an event' && (
              <MayorGeneralStep6Invite setFormSubmitStatus={setFormSubmitStatus} />
            )}

            {/* General enquiry */}
            {currentStep === 2 && EnquiryType === 'general' && (
              <SharedEnquiry
                section="Section 1 of 3"
                header="About your general enquiry"
                legend="What is your general enquiry about?"
              />
            )}
            {currentStep === 3 && EnquiryType === 'general' && (
              <SharedName section="Section 2 of 3" header="About you" />
            )}
            {currentStep === 4 && EnquiryType === 'general' && (
              <SharedContact section="Section 3 of 3" header="About you" />
            )}
            {currentStep === 5 && EnquiryType === 'general' && (
              <GeneralConfirmation setFormSubmitStatus={setFormSubmitStatus} />
            )}

            {/* Strategy */}
            {currentStep === 2 && EnquiryType === 'strategy' && <StrategyEnquiryType />}
            {currentStep === 3 && StrategyEnquiry === 'Culture and Digital' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Culture and Digital strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Economy and Innovation' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Economy and Innovation strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Environment and Energy' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Environment and Energy strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Housing and Land' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Housing and Land strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Inclusive Communities' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Inclusive Communities strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Productivity and Skills' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Productivity and Skills strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Transport' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Transport strategy?"
              />
            )}
            {currentStep === 3 && StrategyEnquiry === 'Wellbeing' && (
              <SharedEnquiry
                section="Section 2 of 4"
                header="About your enquiry"
                legend="What is your enquiry regarding Wellbeing strategy?"
              />
            )}
            {currentStep === 4 && EnquiryType === 'strategy' && (
              <SharedName section="Section 3 of 4" header="About you" />
            )}
            {currentStep === 5 && EnquiryType === 'strategy' && (
              <SharedContact section="Section 4 of 4" header="About you" />
            )}
            {currentStep === 6 && EnquiryType === 'strategy' && (
              <StrategyConfirmation setFormSubmitStatus={setFormSubmitStatus} />
            )}

            {/* Other enquiry type */}
            {currentStep === 2 && EnquiryType === 'other' && <OtherEnquiryType />}

            {/* Foi */}
            {currentStep === 3 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Freedom of information request' && <OtherFoi />}
            {currentStep === 4 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Freedom of information request' && (
                <SharedName section="Section 3 of 4" header="About you" />
              )}
            {currentStep === 5 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Freedom of information request' && (
                <SharedContact section="Section 4 of 4" header="About you" />
              )}
            {currentStep === 6 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Freedom of information request' && (
                <OtherFoiConfirmation setFormSubmitStatus={setFormSubmitStatus} />
              )}

            {/* Fraud */}
            {currentStep === 3 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Whistleblowing and fraud' && <OtherFraud />}
            {currentStep === 4 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Whistleblowing and fraud' && (
                <SharedName section="Section 3 of 4" header="About you" />
              )}
            {currentStep === 5 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Whistleblowing and fraud' && (
                <SharedContact section="Section 4 of 4" header="About you" />
              )}
            {currentStep === 6 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Whistleblowing and fraud' && (
                <OtherFraudConfirmation setFormSubmitStatus={setFormSubmitStatus} />
              )}

            {/* Jobs */}
            {currentStep === 3 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Jobs at the WMCA' && <OtherJobs />}
            {currentStep === 4 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Jobs at the WMCA' && (
                <SharedName section="Section 3 of 4" header="About you" />
              )}
            {currentStep === 5 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Jobs at the WMCA' && (
                <SharedContact section="Section 4 of 4" header="About you" />
              )}
            {currentStep === 6 &&
              EnquiryType === 'other' &&
              OtherEnquiry === 'Jobs at the WMCA' && (
                <OtherJobsConfirmation setFormSubmitStatus={setFormSubmitStatus} />
              )}

            {/* for testing only */}
            {/* {currentStep === 10 && <SubmitSuccess />}
            {currentStep === 11 && <SubmitError />} */}
          </div>
        </div>
        {/* If in development based on envs then show form debugging */}
        {process.env.NODE_ENV !== 'production' && (
          <div
            className="wmcads-col-1 wmcads-col-md-1-4 wmcads-p-md"
            style={{
              overflowX: 'auto',
              position: 'fixed',
              right: 0,
            }}
          >
            <pre>{JSON.stringify(formDataState, null, 2)}</pre>
            <br />
            <div className="wmcads-col-1">
              Select step: {}
              <select
                onChange={(e) =>
                  formDataDispatch({
                    type: 'UPDATE_STEP',
                    payload: +e.target.value,
                  })
                }
                onBlur={(e) =>
                  formDataDispatch({
                    type: 'UPDATE_STEP',
                    payload: +e.target.value,
                  })
                }
                value={currentStep}
              >
                {debugStepOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </FormProvider>
    </>
  );
};

Form.propTypes = {
  formSubmitStatus: PropTypes.bool,
  setFormSubmitStatus: PropTypes.func.isRequired,
  setIsFormStarted: PropTypes.func.isRequired,
  isRecoverLinkPressed: PropTypes.bool,
  setIsRecoverLinkPressed: PropTypes.func.isRequired,
};

Form.defaultProps = {
  formSubmitStatus: null,
  isRecoverLinkPressed: false,
};

export default Form;
